import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Meet Me', path: '/meet-me' },
    { name: 'Personal Styling', path: '/personal-styling' },
    { name: 'Interior Design', path: '/interior-design' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="font-serif text-2xl md:text-3xl text-[#2C3E50] tracking-tight"
          >
            Maison LouLou
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-sans tracking-wide transition-colors duration-300 underline-hover ${
                  isActive(link.path)
                    ? 'text-[#D4A373]'
                    : 'text-[#2C3E50] hover:text-[#D4A373]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-toggle"
                className="text-[#2C3E50]"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#F9F7F2] w-full sm:w-80">
              <div className="flex flex-col gap-8 mt-12">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`font-serif text-2xl transition-colors duration-300 ${
                      isActive(link.path)
                        ? 'text-[#D4A373]'
                        : 'text-[#2C3E50] hover:text-[#D4A373]'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#2C3E50] text-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <h3 className="font-serif text-3xl">Maison LouLou</h3>
            <p className="text-white/70 font-sans text-sm leading-relaxed">
              Elevating everyday living through thoughtful personal styling and interior design.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373]">
              Services
            </h4>
            <div className="flex flex-col gap-3">
              <Link
                to="/personal-styling"
                className="text-white/70 hover:text-white transition-colors text-sm"
              >
                Personal Styling
              </Link>
              <Link
                to="/interior-design"
                className="text-white/70 hover:text-white transition-colors text-sm"
              >
                Interior Design
              </Link>
              <Link
                to="/contact"
                className="text-white/70 hover:text-white transition-colors text-sm"
              >
                Book a Consultation
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373]">
              Connect
            </h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3 text-white/70 text-sm">
                <MapPin className="h-4 w-4" />
                <span>San Diego, California</span>
              </div>
              <a
                href="mailto:hello@maisonloulou.co"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors text-sm"
              >
                <Mail className="h-4 w-4" />
                <span>hello@maisonloulou.co</span>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors text-sm"
              >
                <Instagram className="h-4 w-4" />
                <span>@maisonloulou.co</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-white/50 text-xs font-sans">
            © {new Date().getFullYear()} Maison LouLou. All rights reserved. | maisonloulou.co
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
